import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeaderV2 from "@/components/v2/SiteHeaderV2";
import { Button } from "@/components/ui/button";
import { MODULES, TOTAL_MODULES, getModule } from "@/data/guideV2";
import { COMMUNITY_MODULES, EXPERIENCES } from "@/data/v2Config";
import { useGuideV2Progress } from "@/hooks/useGuideV2Progress";
import { ArrowRight, ArrowUpRight, CheckCircle2, Clock, FlaskConical, ListChecks, Compass, PlayCircle, Wrench } from "lucide-react";

function ProgressRing({ pct, size = 148, stroke = 12 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="v2-progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#FF5F15" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-black text-white tabular">{pct}%</span>
        <span className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">complete</span>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-brand" />
    </div>
    <div>
      <div className="font-heading text-xl font-black text-white leading-none tabular">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  </div>
);

export default function GuideV2Dashboard() {
  const navigate = useNavigate();
  const { count, isDone, actionProgress, worksheetFilled } = useGuideV2Progress();
  const pct = Math.round((count / TOTAL_MODULES) * 100);

  const nextModule = useMemo(() => MODULES.find((m) => !isDone(m.n)) || getModule(TOTAL_MODULES), [count]); // eslint-disable-line
  const worksheetsSaved = useMemo(() => MODULES.filter((m) => worksheetFilled(m.n) > 0).length, [worksheetFilled]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeaderV2 />

      {/* Staging notice */}
      <div className="bg-amber-500/10 border-b border-amber-500/30" data-testid="v2-staging-banner">
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center gap-2 text-xs font-bold text-amber-700">
          <FlaskConical className="w-3.5 h-3.5" /> RouteHaul Pathfinder (Version 2) · active development — not yet live
        </div>
      </div>

      {/* Hero dashboard */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">
            <ProgressRing pct={pct} />
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-brand mb-4">
                <Compass className="w-4 h-4" /> {EXPERIENCES.community.label}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1]">
                Your path to authority, <span className="text-brand">one module at a time.</span>
              </h1>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                This isn't a checklist — it's guided mentorship. Work each module, save your worksheets, and build a real, compliant carrier as you go.
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6">
                <Stat icon={CheckCircle2} value={`${count}/${TOTAL_MODULES}`} label="Missions complete" />
                <Stat icon={ListChecks} value={worksheetsSaved} label="Worksheets started" />
                <Stat icon={Compass} value={nextModule?.phase || "—"} label="Current phase" />
              </div>
            </div>
          </div>

          {/* Continue card */}
          {nextModule && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-10 rounded-2xl bg-white/5 border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center gap-5"
              data-testid="v2-continue-card"
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1">
                  {count === 0 ? "Start here" : count === TOTAL_MODULES ? "You're done — revisit anytime" : "Continue where you left off"}
                </div>
                <div className="font-heading text-xl font-black truncate">Mission {nextModule.n} · {nextModule.title}</div>
                <div className="text-sm text-slate-400 mt-1">{nextModule.tagline}</div>
              </div>
              <Button
                onClick={() => navigate(`/v2/module/${nextModule.n}`)}
                data-testid="v2-continue-btn"
                className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6 shrink-0 group"
              >
                <PlayCircle className="w-5 h-5 mr-2" /> {count === 0 ? "Begin Mission 1" : "Resume"}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Journey */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Your journey</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-slate-900">All {TOTAL_MODULES} missions</h2>
          </div>
          <p className="text-sm text-slate-500 max-w-xs">Tap any mission to open its lesson, worksheet and action plan.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4" data-testid="v2-module-grid">
          {MODULES.map((m, i) => {
            const done = isDone(m.n);
            const ap = actionProgress(m.n, m.checklist.length);
            const started = done || ap.done > 0 || worksheetFilled(m.n) > 0;
            return (
              <motion.div
                key={m.n}
                className="min-w-0"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 2) * 0.05 }}
              >
                <Link
                  to={`/v2/module/${m.n}`}
                  data-testid={`v2-module-card-${m.n}`}
                  className={`group block h-full bg-white rounded-2xl p-6 border transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
                    done ? "border-emerald-300" : "border-slate-200 hover:border-brand"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-heading font-black text-lg shrink-0 ${
                        done ? "bg-emerald-500 text-white" : "bg-slate-900 text-white group-hover:bg-brand transition-colors"
                      }`}>
                        {done ? <CheckCircle2 className="w-6 h-6" /> : String(m.n).padStart(2, "0")}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">{m.phase}</div>
                        <h3 className="font-heading text-base font-bold text-slate-900 leading-tight truncate">{m.title}</h3>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-400 shrink-0">
                      <Clock className="w-3.5 h-3.5" /> {m.duration}
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 mt-4 leading-relaxed">{m.tagline}</p>

                  <div className="mt-5 flex items-center gap-3">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-brand"}`} style={{ width: `${done ? 100 : ap.pct}%` }} />
                    </div>
                    <span className="text-xs font-bold text-slate-500 tabular shrink-0">
                      {done ? "Done" : started ? `${ap.done}/${ap.total} steps` : "Not started"}
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Community modules */}
      <section className="max-w-6xl mx-auto px-6 pb-4">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Your Community</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Everything in your learning experience</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-modules-grid">
          {COMMUNITY_MODULES.map((mod) => {
            const Icon = mod.icon;
            const soon = mod.status !== "available";
            return (
              <Link
                key={mod.key}
                to={mod.to}
                data-testid={`community-module-${mod.key}`}
                className="group block bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-900 group-hover:bg-brand transition-colors flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {soon ? (
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Coming soon</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">Available</span>
                  )}
                </div>
                <h3 className="font-heading text-base font-bold text-slate-900">{mod.label}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{mod.desc}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Pro teaser */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="v2-pro-teaser">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-brand mb-3">
              <Wrench className="w-4 h-4" /> {EXPERIENCES.pro.label}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Ready to put it into action?</h2>
            <p className="mt-2 text-slate-300 leading-relaxed">{EXPERIENCES.pro.blurb} Learn it in a mission, then use a Pro tool to run the numbers.</p>
          </div>
          <Link to="/v2/pro" data-testid="v2-explore-pro" className="shrink-0">
            <Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-12 px-6 group">
              Explore Pro tools <ArrowUpRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Pathfinder</span>
          <span className="font-mono">Version 2 · active development</span>
        </div>
      </footer>
    </div>
  );
}
